import { FormattedMessage, useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { mdiLandPlotsMarker, mdiFlask } from '@mdi/js';
import MdiIcon from '../../components/MdiIcon';
import { linkClick } from '../../components/routerLink';
import type { DiaryDayCounts } from './useDiaryDay';

/** One kind of record, its icon, and where its grid lives under the project. */
const KINDS = [
    {
        key: 'findings' as const,
        labelId: 'finding.title',
        segment: 'findings',
        icon: <LayersOutlinedIcon fontSize="small" />
    },
    {
        key: 'finds' as const,
        labelId: 'find.title',
        segment: 'finds',
        icon: <EmojiEventsIcon fontSize="small" />
    },
    {
        key: 'samples' as const,
        labelId: 'sample.title',
        segment: 'samples',
        icon: <MdiIcon path={mdiFlask} fontSize="small" />
    },
    {
        key: 'drawingSheets' as const,
        labelId: 'drawingSheet.title',
        segment: 'drawing-sheets',
        icon: <ArchitectureIcon fontSize="small" />
    },
    {
        key: 'photos' as const,
        labelId: 'photo.title',
        segment: 'photos',
        icon: <PhotoCameraIcon fontSize="small" />
    },
    {
        key: 'overallDescriptions' as const,
        labelId: 'overallDescription.title',
        segment: 'overall-descriptions',
        icon: <MenuBookIcon fontSize="small" />
    },
    {
        key: 'areaDescriptions' as const,
        labelId: 'areaDescription.title',
        segment: 'area-descriptions',
        icon: <MdiIcon path={mdiLandPlotsMarker} fontSize="small" />
    }
];

/**
 * What else was written up on this Tagebuch day, and a way straight to it: each
 * kind opens its own grid already filtered to the day (see gridDeepLink).
 */
export default function DiaryDayRecordsList({
    counts,
    basePath,
    date,
    onNavigate
}: {
    counts: DiaryDayCounts;
    /** The project's grids live under here, e.g. `/office/projects/<id>`. */
    basePath: string;
    /** The day, as the grids store and filter it ('YYYY-MM-DD'). */
    date: string;
    /** Guard-aware navigation — the form may have unsaved edits. */
    onNavigate: (to: string) => void;
}) {
    const intl = useIntl();
    const total = KINDS.reduce((sum, kind) => sum + counts[kind.key], 0);

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            {total === 0 ? (
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                    >
                        <FormattedMessage id="diary.dayRecords.empty" />
                    </Typography>
                </Box>
            ) : (
                <Stack divider={<Divider />}>
                    {KINDS.map((kind) => {
                        const count = counts[kind.key];
                        const to = `${basePath}/${kind.segment}?date=${date}`;
                        const label = intl.formatMessage({ id: kind.labelId });
                        return (
                            <ListItemButton
                                key={kind.key}
                                // A real link, so the grid can be opened in a
                                // new tab beside the form (see routerLink).
                                component={RouterLink}
                                to={to}
                                onClick={linkClick(() => onNavigate(to))}
                                sx={{ px: 2 }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    {kind.icon}
                                </ListItemIcon>
                                <ListItemText primary={label} />
                                {/* Nothing recorded is worth seeing too — the
                                    row stays, greyed, rather than vanishing. */}
                                <Chip
                                    size="small"
                                    label={count}
                                    color={count > 0 ? 'primary' : 'default'}
                                    variant={count > 0 ? 'filled' : 'outlined'}
                                    sx={{ mr: 1 }}
                                />
                                <OpenInNewIcon
                                    fontSize="small"
                                    sx={{ color: 'text.secondary' }}
                                />
                            </ListItemButton>
                        );
                    })}
                </Stack>
            )}
        </Paper>
    );
}
